import React, { useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonText, IonSpinner, IonLabel,
} from '@ionic/react';
import { closeOutline, cloudUploadOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';

interface ImportModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    importType: 1 | 2;
}

const IMPORT_TITLES: Record<1 | 2, string> = {
    1: 'Import Group and Line Item Detail',
    2: 'Import Inspection Plan Detail',
};

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onDidDismiss, importType }) => {
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0] ?? null;
        setFile(selected);
        setFileError('');
        if (selected) {
            const ext = selected.name.split('.').pop()?.toLowerCase();
            if (ext !== 'xls' && ext !== 'xlsx') {
                setFileError('File must be .xls or .xlsx');
                setFile(null);
            }
        }
    };

    const handleImport = async () => {
        if (!file) {
            setFileError('Please select a file.');
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            await apiService.importContractData(formData, importType);
            setToast({ message: 'Import completed successfully.', variant: 'success' });
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch {
            setToast({ message: 'Import failed. Please check the file and try again.', variant: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setFileError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        onDidDismiss();
    };

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--width': '480px', '--height': '320px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>{IMPORT_TITLES[importType]}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={handleClose}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.5rem', '--padding-end': '1.5rem', '--padding-top': '1.5rem', '--padding-bottom': '1rem' }}>
                    {isUploading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: '1rem' }}>
                            <IonSpinner />
                            <IonText color="medium">Importing...</IonText>
                        </div>
                    ) : (
                        <div>
                            <IonLabel style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                                Import File <span style={{ color: 'var(--ion-color-danger)' }}>*</span>
                            </IonLabel>
                            <IonText color="medium" style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                                Accepted file types: .xls, .xlsx
                            </IonText>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xls,.xlsx"
                                onChange={handleFileChange}
                                style={{ width: '100%', marginBottom: '0.5rem' }}
                            />
                            {fileError && <IonText color="danger" style={{ fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>{fileError}</IonText>}
                            {file && !fileError && (
                                <IonText color="medium" style={{ fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
                                    Selected: {file.name}
                                </IonText>
                            )}
                        </div>
                    )}
                </IonContent>

                {!isUploading && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={handleClose}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" disabled={!file || !!fileError} onClick={handleImport}>
                                    <IonIcon icon={cloudUploadOutline} slot="start" /> Import
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default ImportModal;
